import { History } from 'lucide-react';

import { Button } from '@/components/ui';
import { Textarea } from '@/components/ui/textarea';
import type {
  HumanReviewDecisionResult,
  HumanReviewRequest,
} from '@/lib/admin/client';

import { DecisionButtons } from './DecisionButtons';
import { HumanReviewRequestChips } from './HumanReviewRequestChips';
import { payloadTitle, resolvePayloadRenderer } from './payloadRenderers';

export type HumanReviewRequestCardProps = {
  item: HumanReviewRequest;
  reason: string;
  onReasonChange: (reason: string) => void;
  onDecide: (action: HumanReviewDecisionResult) => void;
  onShowDetails: () => void;
  isDeciding: boolean;
  isSelected: boolean;
};

// Shared list/actions chrome around a per-subjectType payload body. The body is
// resolved from the renderer registry; everything else here (title, note,
// chips, actions) is generic across every subjectType.
export function HumanReviewRequestCard({
  item,
  reason,
  onReasonChange,
  onDecide,
  onShowDetails,
  isDeciding,
  isSelected,
}: HumanReviewRequestCardProps) {
  const title = payloadTitle(item);
  const PayloadBody = resolvePayloadRenderer(item.subjectType);
  const itemIsExpired = item.state === 'expired';

  return (
    <article
      className={`border-border bg-card rounded-md border p-4 ${
        isSelected ? 'ring-ring ring-2 ring-offset-2' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <h2 className="min-w-0 text-base font-semibold">{title}</h2>
        <time className="text-muted-foreground shrink-0 text-right text-xs">
          {new Date(item.createdAt).toLocaleString()}
        </time>
      </div>
      <div className="mt-2">
        <PayloadBody item={item} />
      </div>
      <div className="mt-4 space-y-3">
        <Textarea
          value={reason}
          onChange={(event) => onReasonChange(event.target.value)}
          placeholder={itemIsExpired ? 'Reopen note' : 'Decision note'}
          aria-label={`Decision note for ${title}`}
        />
        <div className="flex flex-wrap items-end justify-between gap-3">
          <HumanReviewRequestChips item={item} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onShowDetails}
            >
              <History className="h-4 w-4" aria-hidden="true" />
              Details
            </Button>
            <DecisionButtons
              onDecide={onDecide}
              disabled={isDeciding}
              state={item.state}
              correction={item.decisionCount > 0}
            />
          </div>
        </div>
      </div>
    </article>
  );
}
